import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useMutation } from '@tanstack/react-query';
import { apiErrorMessage } from '@shared/api/client';
import { uploadDocument } from '@shared/api/documents';
import { createPaymentOrder, verifyPayment } from '@shared/api/payments';
import { useMe } from '@shared/hooks/use-me';
import {
  deriveMessageStatus, useChatHistory, useChatSocket, useMarkRead,
  useMyReceipt, useSendMessage, useThreadReceipts,
} from '@shared/hooks/use-chat';
import {
  useMessageReactions,
  usePinnedMessages,
  useStarredMessages,
} from '@shared/hooks/use-chat-extras';
import {
  useTransaction,
  useUpdateNickName,
  useUpdateNarration,
} from '@shared/hooks/use-transaction-actions';
import { useSendWalletDocumentToChat } from '@shared/hooks/use-wallet-documents';
import type { ChatMessage, ReadReceipt, Uuid, WalletDocumentResponse } from '@shared/types/api';
import { openCheckout } from '@/lib/razorpay';
import { cn } from '@/lib/cn';
import { Composer, type ReplyTarget } from './composer';
import { MessageBubble } from './message-bubble';
import { PinnedBar } from './pinned-bar';
import { SearchBar } from './search-bar';
import { StatusBadge } from './status-badge';
import { TransactionActions } from './transaction-actions';
import { AssociateTransactionActions } from '@/features/associate/web/associate-transaction-actions';
import { WalletDocumentSelectorModal } from './wallet-document-selector';
import { AssociateVerificationModal } from './associate-verification-modal';
import { RequiredDocumentsModal } from './required-documents-modal';

// States where TransactionActions renders at least one button. Mirrors the
// `can*` flags inside TransactionActions itself — kept here as a cheap
// short-circuit so the action-bar row doesn't take vertical space on states
// with no available client action (COMPLETE, CANCELLED, CLOSED, SETTLED).
const STATES_WITH_CLIENT_ACTIONS = new Set([
  'INITIATED',
  'SERVICE_REQUESTED',
  'CHAT_CREATED',
  'INVOICE_CREATED',
  'PAYMENT_PENDING',
  'PAID',
  'PAYMENT_RECEIVED',
  'ASSOCIATE_ASSIGNMENT_PENDING',
  'ASSOCIATE_ASSIGNED',
  'ASSOCIATE_ACCEPTANCE_PENDING',
  'ASSOCIATE_ACCEPTED',
  'CLIENT_CONFIRMATION_PENDING',
  'CLIENT_CONFIRMED',
  'IN_PROGRESS',
  'DRAFT_PREPARATION',
  'CLIENT_DRAFT_CONFIRMATION',
  'FINAL_RESULT_RECEIVED',
  'DELIVERED',
  'CLIENT_CONFIRMED_DEEMED_SATISFIED',
  'GRIEVANCE_RAISED',
  'CORRECTION_PENDING',
  'RATING_PENDING',
  'SETTLEMENT_PENDING',
  'ASSIGNED',
  'ASSIGNED_REVOKED',
  'REVIEW',
  'REVISION_REQUESTED',
  'SENT_FOR_APPROVAL',
  'FINAL_DOC_READY',
]);
function hasClientActions(status: string): boolean {
  return STATES_WITH_CLIENT_ACTIONS.has(status);
}

function sameDay(a: string, b: string) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}
function dayLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  if (sameDay(iso, today.toISOString())) return 'Today';
  if (sameDay(iso, y.toISOString())) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ChatThread({ transactionId }: { transactionId: Uuid }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: tx } = useTransaction(transactionId);

  const updateNickName = useUpdateNickName(transactionId);
  const [isEditingNickName, setIsEditingNickName] = useState(false);
  const [nickNameValue, setNickNameValue] = useState('');
  const updateNarration = useUpdateNarration(transactionId);
  const [isEditingNarration, setIsEditingNarration] = useState(false);
  const [narrationValue, setNarrationValue] = useState('');
  const history = useChatHistory(transactionId);
  const { connected, typingUserIds, sendTyping } = useChatSocket(transactionId);
  const send = useSendMessage(transactionId);
  const markRead = useMarkRead(transactionId);
  const { data: receipt } = useMyReceipt(transactionId);
  const { data: threadReceipts } = useThreadReceipts(transactionId);
  const peerReceipts = threadReceipts?.receipts ?? [];

  const pinned = usePinnedMessages(transactionId);
  const starred = useStarredMessages();
  const reactions = useMessageReactions(transactionId);
  const isPaid = tx
    ? tx.status !== 'INITIATED' &&
    tx.status !== 'SERVICE_REQUESTED' &&
    tx.status !== 'CHAT_CREATED' &&
    tx.status !== 'INVOICE_CREATED' &&
    tx.status !== 'PAYMENT_PENDING' &&
    tx.status !== 'CANCELLED'
    : false;
  const isAssociateWorker = tx && me ? tx.assignedAssociateId === me.id : false;
  const isClientForTx = tx && me ? tx.clientId === me.id : !isAssociateWorker;
  const isAssociatePath = window.location.pathname.startsWith('/associate');
  const backHref = isAssociatePath ? '/associate/chat' : '/client/chat';

  const [reply, setReply] = useState<ReplyTarget | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [starredOnly, setStarredOnly] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [reqDocsModalOpen, setReqDocsModalOpen] = useState(false);
  const [selectedWalletDocs, setSelectedWalletDocs] = useState<WalletDocumentResponse[]>([]);
  const sendWalletDoc = useSendWalletDocumentToChat(transactionId);

  const canEditRequiredDocs =
    (isAssociatePath ||
      (me && (me.role === 'ADMIN' || me.role === 'ASSOCIATE' || me.role === 'FRANCHISE'))) &&
    !isClientForTx;

  function handleSelectWalletDocs(newDocs: WalletDocumentResponse[]) {
    setSelectedWalletDocs((existing) => {
      const map = new Map(existing.map((d) => [d.id, d]));
      for (const d of newDocs) map.set(d.id, d);
      return Array.from(map.values());
    });
  }

  function handleRemoveWalletDoc(id: string) {
    setSelectedWalletDocs((existing) => existing.filter((d) => d.id !== id));
  }

  useEffect(() => {
    if (location.state && (location.state as any).showWelcome) {
      setWelcomeOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const prevNewestRef = useRef<string | null>(null);

  const allMessages = useMemo(() => {
    const items = history.data?.items ?? [];
    const reversed = [...items].reverse();

    let latestProposedId: string | null = null;
    let latestFinalizedId: string | null = null;

    for (let i = reversed.length - 1; i >= 0; i--) {
      const m = reversed[i];
      const kind = m.metadata?.kind as string | undefined;
      const isScopeForm =
        m.type === 'SCOPE_FORM' ||
        (kind?.startsWith('scope_form') && kind !== 'scope_form:accepted');

      if (isScopeForm) {
        const isProposed = kind === 'scope_form:proposed' || m.metadata?.status === 'PROPOSED';
        if (isProposed) {
          if (!latestProposedId) latestProposedId = m.id;
        } else {
          if (!latestFinalizedId) latestFinalizedId = m.id;
        }
      }
    }

    if (!latestProposedId && !latestFinalizedId) return reversed;

    return reversed.filter((m) => {
      const kind = m.metadata?.kind as string | undefined;
      const isScopeForm =
        m.type === 'SCOPE_FORM' ||
        (kind?.startsWith('scope_form') && kind !== 'scope_form:accepted');

      if (isScopeForm) {
        const isProposed = kind === 'scope_form:proposed' || m.metadata?.status === 'PROPOSED';
        if (isProposed && latestProposedId && m.id !== latestProposedId) {
          return false;
        }
        if (!isProposed && latestFinalizedId && m.id !== latestFinalizedId) {
          return false;
        }
      }
      return true;
    });
  }, [history.data?.items]);

  const pinnedList = useMemo(
    () => allMessages.filter((m) => pinned.set.has(m.id)),
    [allMessages, pinned.set],
  );

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return allMessages.filter((m) => {
      if (starredOnly && !starred.set.has(m.id)) return false;
      if (!q) return true;
      return (m.body ?? '').toLowerCase().includes(q);
    });
  }, [allMessages, searchQ, starredOnly, starred.set]);

  const newestId = allMessages[allMessages.length - 1]?.id ?? null;
  const lastMarkedReadIdRef = useRef<string | null>(null);
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  useEffect(() => {
    if (!newestId) return;
    const cursor = receipt && 'lastReadMessageId' in receipt ? receipt.lastReadMessageId : null;
    if (prevNewestRef.current !== newestId) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      prevNewestRef.current = newestId;
    }
    if (
      cursor !== newestId &&
      lastMarkedReadIdRef.current !== newestId &&
      !markReadRef.current.isPending
    ) {
      lastMarkedReadIdRef.current = newestId;
      markReadRef.current.mutate(newestId);
    }
  }, [newestId, receipt]);

  const upload = useMutation({
    mutationFn: (file: File) => uploadDocument(transactionId, file, 'SUPPORTING'),
  });

  async function handlePay(invoiceId: string) {
    setPayError(null);
    setPaying(true);
    try {
      const order = await createPaymentOrder(invoiceId);
      const result = await openCheckout({
        keyId: order.keyId,
        orderId: order.providerOrderId,
        amountPaise: order.amountPaise,
        currency: order.currency,
        name: 'EcRegi',
        description: `Invoice ${order.invoice.invoiceNumber}`,
        prefill: order.prefill,
      });
      await verifyPayment(result);
    } catch (err) {
      setPayError(apiErrorMessage(err, (err as Error).message));
    } finally {
      setPaying(false);
    }
  }

  function jumpTo(id: string) {
    const el = messageRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.animate(
      [
        { boxShadow: '0 0 0 3px rgba(234, 179, 8, 0.55)' },
        { boxShadow: '0 0 0 3px rgba(234, 179, 8, 0)' },
      ],
      { duration: 1200 },
    );
  }

  async function handleSend(
    body: string,
    file: File | null,
    walletDocs: WalletDocumentResponse[],
    extraMetadata?: Record<string, unknown>,
  ) {
    const metadata: Record<string, unknown> = { ...(extraMetadata || {}) };
    if (reply) {
      metadata.replyTo = { id: reply.id, body: reply.body, senderName: reply.senderName };
    }

    if (walletDocs.length > 0) {
      try {
        for (const doc of walletDocs) {
          await sendWalletDoc.mutateAsync(doc.id);
        }
        if (body) {
          send.mutate({ body, metadata: Object.keys(metadata).length ? metadata : undefined });
        }
      } catch (err) {
        alert(apiErrorMessage(err, 'Failed to send wallet document(s) into chat'));
      }
      setSelectedWalletDocs([]);
      setReply(null);
      return;
    }

    if (file) {
      upload.mutate(file, {
        onSuccess: (doc) => {
          send.mutate({
            body: body || undefined,
            attachmentDocumentId: doc.id,
            metadata: {
              fileName: doc.fileName,
              mimeType: doc.mimeType,
              ...metadata,
            },
          });
        },
      });
    } else {
      send.mutate({ body, metadata: Object.keys(metadata).length ? metadata : undefined });
    }
    setReply(null);
  }

  if (!tx) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
        Loading thread…
      </div>
    );
  }

  const otherTyping = [...typingUserIds].filter((id) => id !== me?.id);

  const isPendingAcceptance = isAssociateWorker && tx?.associateAssignmentStatus === 'PENDING';
  const isPendingClientVerification =
    isClientForTx && tx?.associateVerificationStatus === 'PENDING_CLIENT_VERIFICATION';
  const isMaxRejectionsReached =
    isClientForTx &&
    ((tx?.associateRejectionCount ?? 0) >= 2 ||
      (tx?.status === 'CANCELLED' &&
        (tx?.cancellationReason?.toLowerCase().includes('associate changes') ?? false)));

  return (
    <section
      className="flex h-full min-h-0 flex-1 min-w-0 flex-col relative overflow-hidden"
      style={{
        backgroundColor: '#efeae2',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath d='M9 24c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4zm30 18c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4zM12 36c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm28-24c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-16 10c0-1.7 1.3-3 3-3s3 1.3 3 3-1.3 3-3 3-3-1.3-3-3zm18 18c0-1.7 1.3-3 3-3s3 1.3 3 3-1.3 3-3 3-3-1.3-3-3z' fill='%23e5ddd5' fill-opacity='0.45' fill-rule='evenodd'/%3E%3C/svg%3E")`,
      }}
    >
      <header className="border-b border-gray-200 bg-white min-w-0 shrink-0">
        {/* Top row: back button (mobile) + Avatar + title + icon toolbar */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-5 sm:py-2.5 min-w-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Link
              to={backHref}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 md:hidden"
              aria-label="Back to conversations"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </Link>

            {/* WhatsApp-style Header Avatar */}
            <div className="shrink-0 h-9 w-9 rounded-full overflow-hidden bg-brand-100 flex items-center justify-center border border-gray-100">
              {tx.service.images && tx.service.images[0] ? (
                <img
                  src={tx.service.images[0]}
                  alt={tx.service.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-sm font-semibold uppercase text-brand-800">
                  {tx.assignedAssociate
                    ? (tx.assignedAssociate.firstName?.slice(0, 1) ?? 'A')
                    : tx.service.name.slice(0, 1)}
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1 ml-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-sm font-semibold text-gray-900 leading-tight">
                  {tx.service.name}
                </h2>
                <StatusBadge status={tx.status} />

                {/* Nickname Editor / Display - Associate Worker Only */}
                {isAssociatePath && !isClientForTx ? (
                  isEditingNickName ? (
                    <input
                      type="text"
                      value={nickNameValue}
                      onChange={(e) => setNickNameValue(e.target.value)}
                      onBlur={() => {
                        const trimmed = nickNameValue.trim();
                        if (trimmed !== (tx.nickName ?? '')) {
                          updateNickName.mutate(trimmed || null);
                        }
                        setIsEditingNickName(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = nickNameValue.trim();
                          if (trimmed !== (tx.nickName ?? '')) {
                            updateNickName.mutate(trimmed || null);
                          }
                          setIsEditingNickName(false);
                        } else if (e.key === 'Escape') {
                          setIsEditingNickName(false);
                        }
                      }}
                      placeholder="Type nickname..."
                      className="h-6 px-2 text-xs font-medium text-gray-900 rounded border border-gray-300 focus:border-brand-500 focus:outline-none bg-white shadow-xs"
                      autoFocus
                    />
                  ) : tx.nickName ? (
                    <button
                      type="button"
                      onClick={() => {
                        setNickNameValue(tx.nickName ?? '');
                        setIsEditingNickName(true);
                      }}
                      className="text-xs font-semibold text-gray-700 hover:text-brand-700 cursor-pointer"
                      title="Click to edit nickname"
                    >
                      ({tx.nickName})
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setNickNameValue('');
                        setIsEditingNickName(true);
                      }}
                      className="text-xs font-normal text-gray-400 hover:text-brand-700 hover:underline"
                      title="Click to add nickname"
                    >
                      + Add nickname
                    </button>
                  )
                ) : null}

                {/* Narration Display - Desktop Client View (Placed near status badge) */}
                {(!isAssociatePath || isClientForTx) && tx.narration ? (
                  <div
                    className="hidden sm:flex items-center gap-1 bg-brand-50 border border-brand-200/80 text-brand-900 px-2.5 py-0.5 rounded-md text-xs font-medium max-w-xs truncate"
                    title="Status narration"
                  >
                    <span className="font-semibold text-brand-800 shrink-0">Narration:</span>
                    <span className="truncate">{tx.narration}</span>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-2 truncate text-[11px] text-gray-500 leading-normal mt-0.5">
                <span className="font-mono text-gray-400">#{tx.code}</span>
                <span>•</span>
                {connected ? (
                  <span className="text-emerald-600 font-medium">Online</span>
                ) : (
                  <span>Offline</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <IconButton
              title="Document Wallet"
              onClick={() => setWalletModalOpen(true)}
              active={walletModalOpen}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="inline-block align-middle text-emerald-700"
              >
                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                <path d="M9 12h6m-3-3v6" />
              </svg>
            </IconButton>
            <IconButton title="Search" onClick={() => setSearchOpen((v) => !v)} active={searchOpen}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="inline-block align-middle"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </IconButton>
            <IconButton
              title={starredOnly ? 'Show all' : 'Show starred'}
              onClick={() => setStarredOnly((v) => !v)}
              active={starredOnly}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill={starredOnly ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="inline-block align-middle"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </IconButton>
            {!isAssociatePath && (
              <Link
                to={`/client/services/${tx.serviceId}`}
                className="hidden text-xs text-brand-700 hover:underline sm:inline-block"
              >
                View service
              </Link>
            )}
          </div>
        </div>
        {isAssociatePath && !isClientForTx ? (
          // Action bar — transaction state actions + Narration field for Worker Associate
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60 px-3 py-2 sm:px-5 sm:py-2.5">
            <AssociateTransactionActions tx={tx} />
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {isEditingNarration ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-gray-700 shrink-0">Narration:</span>
                  <input
                    type="text"
                    value={narrationValue}
                    onChange={(e) => setNarrationValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const trimmed = narrationValue.trim();
                        if (trimmed !== (tx.narration ?? '')) {
                          updateNarration.mutate(trimmed || null);
                        }
                        setIsEditingNarration(false);
                      } else if (e.key === 'Escape') {
                        setIsEditingNarration(false);
                      }
                    }}
                    placeholder="Status reason..."
                    className="h-7 w-48 max-w-xs px-2.5 text-xs font-medium text-gray-900 rounded-md border border-brand-300 focus:border-brand-600 focus:outline-none bg-white shadow-xs"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const trimmed = narrationValue.trim();
                      if (trimmed !== (tx.narration ?? '')) {
                        updateNarration.mutate(trimmed || null);
                      }
                      setIsEditingNarration(false);
                    }}
                    className="h-7 text-xs font-medium bg-brand-700 text-white px-2.5 rounded-md hover:bg-brand-800 transition-colors shrink-0 cursor-pointer"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingNarration(false)}
                    className="h-7 text-xs font-medium text-gray-500 hover:text-gray-700 px-1.5 shrink-0 cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              ) : tx.narration ? (
                <div className="flex items-center gap-2 shrink-0">
                  <div
                    className="bg-brand-50 border border-brand-200/80 text-brand-900 px-2.5 py-1 rounded-md text-xs font-medium max-w-full sm:max-w-md flex items-center gap-1"
                    title={tx.narration}
                  >
                    <span className="font-semibold text-brand-800 shrink-0">Narration:</span>
                    <span className="break-words">{tx.narration}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setNarrationValue(tx.narration ?? '');
                      setIsEditingNarration(true);
                    }}
                    className="h-7 px-2.5 text-xs font-medium border border-brand-300 bg-white hover:bg-brand-50 text-brand-700 hover:text-brand-900 rounded-md shadow-xs transition-colors cursor-pointer shrink-0 flex items-center justify-center"
                    title="Edit narration"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setNarrationValue('');
                    setIsEditingNarration(true);
                  }}
                  className="h-7 text-xs font-medium bg-brand-700 text-white hover:bg-brand-800 px-2.5 rounded-md transition-colors shadow-xs shrink-0 cursor-pointer"
                  title="Click to add status narration"
                >
                  Add narration
                </button>
              )}
            </div>
          </div>
        ) : (isClientForTx || !isAssociatePath) && (hasClientActions(tx.status) || tx.narration) ? (
          // Action bar — transaction state actions + Narration display for Client
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60 px-3 py-2 sm:px-5 sm:py-2.5">
            <TransactionActions
              transactionId={transactionId}
              status={tx.status}
              paidAt={tx.paidAt}
              cancellationRequestStatus={tx.cancellationRequestStatus}
            />
            {tx.narration ? (
              <div
                className="sm:hidden ml-auto bg-brand-50 border border-brand-200/80 text-brand-900 px-2.5 py-1 rounded-md text-xs font-medium shrink-0 flex items-center gap-1 max-w-full"
                title="Status narration"
              >
                <span className="font-semibold text-brand-800 shrink-0">Narration:</span>
                <span>{tx.narration}</span>
              </div>
            ) : null}
          </div>
        ) : null}
        {searchOpen ? (
          <SearchBar
            value={searchQ}
            onChange={setSearchQ}
            onClose={() => {
              setSearchOpen(false);
              setSearchQ('');
            }}
            matchCount={filtered.length}
          />
        ) : null}
        <PinnedBar messages={pinnedList} onJumpTo={jumpTo} onUnpin={(id) => pinned.toggle(id)} />
      </header>

      {tx.cancellationRequestStatus === 'PENDING' ? (
        <div className="flex items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 shrink-0 shadow-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded">
              Grievance Pending
            </span>
            <span>
              Your cancellation request is under Admin review.
              {tx.cancellationRequestReason ? (
                <span className="italic font-medium text-amber-950">
                  {' '}
                  Reason: "{tx.cancellationRequestReason}"
                </span>
              ) : (
                ''
              )}
            </span>
          </div>
        </div>
      ) : null}

      {isPendingAcceptance ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900 shrink-0 shadow-xs">
          <span className="font-medium">
            You have been assigned to this service request. Please accept the assignment on the
            Transactions page before sending messages.
          </span>
          <Link
            to="/associate/transactions"
            className="rounded bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 transition-colors"
          >
            Go to Transactions
          </Link>
        </div>
      ) : null}

      {isPendingClientVerification ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-300 bg-brand-50 px-4 py-2.5 text-xs text-brand-900 shrink-0 shadow-xs">
          <div className="flex items-center gap-2">
            <span className="flex h-2.5 w-2.5 rounded-full bg-brand-600 animate-pulse" />
            <span className="font-semibold text-brand-950">
              Associate Assigned! Please verify your associate to begin work.
            </span>
          </div>
          <Button
            type="button"
            onClick={() => setVerifyModalOpen(true)}
            className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-xs rounded-md"
          >
            Verify Associate
          </Button>
        </div>
      ) : null}

      {isMaxRejectionsReached ? (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-b border-red-300 bg-red-50 px-4 py-3 text-xs text-red-900 shrink-0 shadow-xs">
          <div className="flex items-center gap-2">
            <span className="text-base">⚠️</span>
            <span className="font-semibold">
              You have reached the maximum associate change limit (2/2). This service request has
              been closed.
            </span>
          </div>
          <Link
            to={`/client/services/${tx.serviceId}`}
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 transition-colors shadow-xs shrink-0"
          >
            Start Fresh Service Request
          </Link>
        </div>
      ) : null}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 sm:px-5 min-h-0">
        {history.isLoading ? (
          <p className="text-center text-sm text-gray-500">Loading messages…</p>
        ) : filtered.length === 0 ? (
          searchQ || starredOnly ? (
            <p className="mt-8 text-center text-sm text-gray-500">No messages match.</p>
          ) : (
            <EmptyThread />
          )
        ) : (
          <MessageList
            messages={filtered}
            myId={me?.id}
            peerReceipts={peerReceipts}
            messageRefs={messageRefs}
            canFetchOlder={history.data?.hasMore ?? false}
            onFetchOlder={() => history.fetchOlder()}
            pinnedSet={pinned.set}
            starredSet={starred.set}
            reactionsMap={reactions.map}
            onReply={(m) =>
              setReply({
                id: m.id,
                body: m.body,
                // Use the server-supplied sender role/name; fall back to
                // "you" for own messages and a generic label otherwise.
                senderName:
                  m.senderUserId === me?.id
                    ? 'you'
                    : (m.sender?.firstName ?? (m.sender?.role === 'ADMIN' ? 'Admin' : 'Associate')),
              })
            }
            onCopy={(m) => {
              if (m.body) navigator.clipboard?.writeText(m.body).catch(() => { });
            }}
            onTogglePin={pinned.toggle}
            onToggleStar={starred.toggle}
            onReact={reactions.toggle}
            onJumpTo={jumpTo}
            onPay={handlePay}
            isPaid={isPaid}
            canEditRequiredDocs={canEditRequiredDocs}
            onEditRequiredDocs={() => setReqDocsModalOpen(true)}
          />
        )}
        {otherTyping.length > 0 ? (
          <div className="mt-3 text-xs italic text-gray-500">Someone is typing…</div>
        ) : null}
        {payError ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {payError}
          </div>
        ) : null}
        {paying ? (
          <div className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
            Opening checkout…
          </div>
        ) : null}
        {upload.isError ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            Upload failed: {apiErrorMessage(upload.error, (upload.error as Error)?.message)}
          </div>
        ) : null}
        {upload.isPending ? (
          <div className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
            Uploading attachment…
          </div>
        ) : null}
        {sendWalletDoc.isError ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            Failed to send wallet document: {apiErrorMessage(sendWalletDoc.error)}
          </div>
        ) : null}
        {sendWalletDoc.isPending ? (
          <div className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
            Sending wallet document…
          </div>
        ) : null}
        <div ref={bottomRef} />
      </div>

      <Composer
        disabled={
          send.isPending ||
          upload.isPending ||
          sendWalletDoc.isPending ||
          tx?.status === 'COMPLETE' ||
          tx?.status === 'CANCELLED' ||
          isPendingAcceptance ||
          isPendingClientVerification ||
          isMaxRejectionsReached
        }
        placeholder={
          isPendingAcceptance
            ? 'Accept assignment on transactions page to send messages'
            : isPendingClientVerification
              ? 'Please verify associate to send messages'
              : isMaxRejectionsReached
                ? 'Associate change limit reached. Request closed.'
                : undefined
        }
        onSend={handleSend}
        onTypingChange={sendTyping}
        replyTarget={reply}
        onClearReply={() => setReply(null)}
        onOpenWallet={() => setWalletModalOpen(true)}
        selectedWalletDocs={selectedWalletDocs}
        onClearWalletDocs={() => setSelectedWalletDocs([])}
        onRemoveWalletDoc={handleRemoveWalletDoc}
      />

      <WalletDocumentSelectorModal
        open={walletModalOpen}
        onClose={() => setWalletModalOpen(false)}
        onSelectDocuments={handleSelectWalletDocs}
        alreadySelectedIds={selectedWalletDocs.map((d) => d.id)}
      />

      <AssociateVerificationModal
        open={verifyModalOpen}
        onClose={() => setVerifyModalOpen(false)}
        transactionId={transactionId}
      />

      <RequiredDocumentsModal
        open={reqDocsModalOpen}
        onClose={() => setReqDocsModalOpen(false)}
        transactionId={transactionId}
        currentDocs={tx.service.requiredDocuments ?? []}
      />

      {welcomeOpen && tx && (
        <Dialog
          open={welcomeOpen}
          onClose={() => setWelcomeOpen(false)}
          title="Welcome to EcRegi!"
          footer={
            <Button
              onClick={() => setWelcomeOpen(false)}
              className="w-full sm:w-auto bg-brand-700 hover:bg-brand-800 text-white font-medium"
            >
              Okay, got it!
            </Button>
          }
        >
          <div className="space-y-4 py-2 text-sm text-gray-700 leading-relaxed">
            <p className="font-medium text-gray-900 text-base">
              Welcome to the {tx.service.name} service!
            </p>
            <p>
              We are glad to help you today. An associate will be assigned to guide you through the
              process shortly.
            </p>
            {tx.service.requiredDocuments && tx.service.requiredDocuments.length > 0 ? (
              <div className="rounded-lg border border-brand-100 bg-brand-50/30 p-3.5 mt-3">
                <p className="font-semibold text-xs uppercase tracking-wide text-brand-800 mb-2">
                  Required Documents Checklist
                </p>
                <ul className="list-inside list-disc space-y-1 text-xs text-gray-800">
                  {tx.service.requiredDocuments.map((doc: string) => (
                    <li key={doc} className="font-medium">
                      {doc}
                    </li>
                  ))}
                </ul>
                <p className="mt-2.5 text-[10px] text-brand-600 font-medium">
                  Please keep these documents ready to upload in the chat.
                </p>
              </div>
            ) : null}
          </div>
        </Dialog>
      )}
    </section>
  );
}

function IconButton({
  title,
  onClick,
  active,
  children,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'rounded-md px-2 py-1 text-sm',
        active ? 'bg-brand-50 text-brand-700' : 'text-gray-500 hover:bg-gray-100',
      )}
    >
      {children}
    </button>
  );
}

type ListProps = {
  messages: ChatMessage[];
  myId: string | undefined;
  peerReceipts: ReadReceipt[];
  messageRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  canFetchOlder: boolean;
  onFetchOlder: () => void;
  pinnedSet: Set<string>;
  starredSet: Set<string>;
  reactionsMap: Record<string, string[]>;
  onReply: (m: ChatMessage) => void;
  onCopy: (m: ChatMessage) => void;
  onTogglePin: (id: string) => void;
  onToggleStar: (id: string) => void;
  onReact: (id: string, emoji: string) => void;
  onJumpTo: (id: string) => void;
  onPay: (invoiceId: string) => void;
  isPaid?: boolean;
  canEditRequiredDocs?: boolean;
  onEditRequiredDocs?: () => void;
};

function MessageList({
  messages,
  myId,
  peerReceipts,
  messageRefs,
  canFetchOlder,
  onFetchOlder,
  pinnedSet,
  starredSet,
  reactionsMap,
  onReply,
  onCopy,
  onTogglePin,
  onToggleStar,
  onReact,
  onJumpTo,
  onPay,
  isPaid,
  canEditRequiredDocs,
  onEditRequiredDocs,
}: ListProps) {
  const rows: React.ReactNode[] = [];
  let lastDate = '';
  let lastSender: string | null | undefined = undefined;

  for (const m of messages) {
    if (!lastDate || !sameDay(m.createdAt, lastDate)) {
      rows.push(
        <div key={`d-${m.id}`} className="my-3 flex items-center justify-center">
          <span className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
            {dayLabel(m.createdAt)}
          </span>
        </div>,
      );
      lastDate = m.createdAt;
      lastSender = undefined;
    }
    const isMine = !!myId && m.senderUserId === myId;
    const showSender = m.senderUserId !== lastSender;
    rows.push(
      <div
        key={m.id}
        ref={(el) => (messageRefs.current[m.id] = el)}
        // WhatsApp-ish spacing: a clear break when the speaker changes,
        // tighter stacking for grouped messages from the same sender.
        className={cn(showSender ? 'mt-3' : 'mt-0.5')}
      >
        <MessageBubble
          message={m}
          isMine={isMine}
          showSender={showSender}
          isPinned={pinnedSet.has(m.id)}
          isStarred={starredSet.has(m.id)}
          reactions={reactionsMap[m.id] ?? []}
          deliveryStatus={isMine ? deriveMessageStatus(m, peerReceipts, myId) : null}
          onReply={onReply}
          onCopy={onCopy}
          onTogglePin={onTogglePin}
          onToggleStar={onToggleStar}
          onReact={onReact}
          onJumpTo={onJumpTo}
          onPay={isPaid ? undefined : onPay}
          canEditRequiredDocs={canEditRequiredDocs}
          onEditRequiredDocs={onEditRequiredDocs}
        />
      </div>,
    );
    lastSender = m.senderUserId;
  }

  return (
    <div className="space-y-1">
      {canFetchOlder ? (
        <div className="mb-3 text-center">
          <button
            type="button"
            className="text-xs text-brand-700 hover:underline"
            onClick={onFetchOlder}
          >
            Load older messages
          </button>
        </div>
      ) : null}
      {rows}
    </div>
  );
}

function EmptyThread() {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-brand-50">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#15803d"
          strokeWidth="1.75"
          aria-hidden
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900">Say hello</h3>
      <p className="mt-1 max-w-xs text-sm text-gray-600">
        Messages with your Associate show up here. Drop a note to get started.
      </p>
    </div>
  );
}
